import React from 'react'
import styles from './Badge.module.scss'

export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral' | 'live'

interface BadgeProps {
  variant: BadgeVariant
  children: React.ReactNode
  className?: string
  /** When true, renders badge as a clickable button with cursor pointer */
  clickable?: boolean
  /** Click handler for clickable badges */
  onClick?: () => void
  /** Icon to display after the badge text: 'clipboard', 'check', or 'link' */
  icon?: 'clipboard' | 'check' | 'link'
  /** Accessible label for the clickable badge */
  ariaLabel?: string
}

const ClipboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.icon}>
    <rect x="9" y="2" width="6" height="4" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
  </svg>
)

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.icon}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const LinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.icon}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
)

/**
 * Badge component for status indicators, categories, and labels
 * 
 * Variants:
 * - primary: Green (e.g., sport names)
 * - secondary: Blue (e.g., sources, info)
 * - success: Green (e.g., completed, active)
 * - warning: Yellow (e.g., pending, in progress)
 * - error: Red (e.g., failed, cancelled)
 * - neutral: Gray (e.g., general labels)
 */
export const Badge: React.FC<BadgeProps> = ({ variant, children, className = '', clickable, onClick, icon, ariaLabel }) => {
  const classes = `${styles.badge} ${styles[variant]} ${clickable ? styles.clickable : ''} ${className}`

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (clickable && onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <span
      className={classes}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? ariaLabel : undefined}
    >
      {children}
      {icon === 'clipboard' && <ClipboardIcon />}
      {icon === 'check' && <CheckIcon />}
      {icon === 'link' && <LinkIcon />}
    </span>
  )
}

